import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { S3_BUCKET_URL } from '@/constants';
import { LAYOUT_NOTES } from '@/constants/campaign-notes';
import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSprite,
  OptionItemSpriteList,
} from '@/pages/Onboarding/components/OptionItemSprite/OptionItemSprite';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import {
  AGE_OPTIONS,
  AGE_OPTIONS_COMPACT,
} from '@/pages/Onboarding/options/Age/AGE_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';
import { ETHNICITY_OPTIONS } from '../Ethnicity/ETHNICITY_OPTIONS';

import { ScreenAgeStyles } from './Age.styles';

// Get image path based on layout notes
const getImagePath = (ageId: string, ethnicityId: string) => {
  const layoutNotes = webCampaign.getLayoutNotes();
  const isCompactOnboarding = layoutNotes.includes(
    LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS,
  );

  if (isCompactOnboarding) {
    return `${S3_BUCKET_URL}/layout-own-ai-man/ethnicity/compact/sprite-${ageId}-${ethnicityId}.png`;
  }

  return `${S3_BUCKET_URL}/layout-own-ai-man/ethnicity/sprite-${ageId}-${ethnicityId}.png`;
};

const getPreloadArray = () => {
  const layoutNotes = webCampaign.getLayoutNotes();
  const isCompactOnboarding = layoutNotes.includes(
    LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS,
  );

  const ageOptions = isCompactOnboarding ? AGE_OPTIONS_COMPACT : AGE_OPTIONS;

  return ageOptions.reduce<string[]>((acc, ageOption) => {
    const ethnicityImages = ETHNICITY_OPTIONS.map((ethnicityOption) =>
      getImagePath(ageOption.id, ethnicityOption.id),
    );

    acc = [...acc, ...ethnicityImages];

    return acc;
  }, []);
};

export const ScreenAge: React.FC = () => {
  const { user } = useAccount();

  React.useEffect(() => {
    if (user && canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Age',
      });
    }
    getPreloadArray().forEach((src) => {
      preloadImage(src);
    });
  }, [user]);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();
  const age = form.watch('age');

  const handleSelect = (option: OnbOptionType<LayoutDataType['age']>) => {
    form.setValue('age', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        age: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const getAgeOptions = () => {
    const layoutNotes = webCampaign.getLayoutNotes();
    return layoutNotes.includes(LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS)
      ? AGE_OPTIONS_COMPACT
      : AGE_OPTIONS;
  };

  const getSpriteImagePath = (optionId: string) => {
    const layoutNotes = webCampaign.getLayoutNotes();
    const isCompactOnboarding = layoutNotes.includes(
      LAYOUT_NOTES.TOTAL_REAL_BOYS_SCREENS,
    );

    if (isCompactOnboarding) {
      return `${S3_BUCKET_URL}/layout-own-ai-man/age/compact/sprite-${optionId}.png`;
    }

    return `${S3_BUCKET_URL}/layout-own-ai-man/age/sprite-${optionId}.png`;
  };

  const ageOptions = getAgeOptions();

  return (
    <ScreenAgeStyles>
      <OnboardingNavigation />
      <OwnAiTitle>
        Choose your AI partner's <OwnAiTitleHighlight>age</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSpriteList>
          {ageOptions.map((option) => (
            <OptionItemSprite
              key={option.id}
              alt="age"
              isSelected={age === option.id}
              onClick={() => handleSelect(option)}
              spriteSrc={getSpriteImagePath(option.id)}
              title={option.title}
              TEST_ID={`age-${option.id}`}
              show
            />
          ))}
        </OptionItemSpriteList>
      </IonContent>
    </ScreenAgeStyles>
  );
};
